import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarGrid } from './CalendarGrid';
import { CalendarPopper } from './CalendarPopper';
import {
  CalendarToggle,
  FieldMessage,
  FieldRoot,
  FieldShell,
  SegmentInput,
  describeProblem,
  type FieldSize,
  type IconPlacement,
  type TypedProblem,
} from './FieldParts';
import { compareIso, normalizeRange, type IsoDate, type IsoDateRange } from './isoDate';
import { followTypedDay, monthLimits, openingPanels, type MonthIndex } from './panels';
import { PresetList } from './PresetList';
import type { DateRangePreset } from './presets';
import {
  draftsEqual,
  isSettled,
  openRangeEnd,
  pickRangeDay,
  toDraft,
  toRange,
  type Bound,
  type RangeDraft,
} from './rangeDraft';
import { useCalendarPopover } from './useCalendarPopover';

export interface DateRangeChange {
  /** The range was left open-ended: a start and deliberately no end (`value.end` is `null`). */
  openEnded: boolean;
}

export interface DateRangeFieldProps {
  /** Both ends inclusive, each `'YYYY-MM-DD'` or `null`. */
  value: IsoDateRange;
  /** The stored range is open-ended (only read with `allowOpenEnd`). */
  openEnded?: boolean;
  /**
   * The range may be left open-ended: "open" is accepted in the end field, and an
   * "Open end" row sits beside the calendar.
   */
  allowOpenEnd?: boolean;
  /**
   * Fires once per finished edit, with a SETTLED range only — both ends, a start and an
   * open end, or (cleared) neither. A range being picked is the field's own draft and is
   * never published half-made.
   */
  onChange: (value: IsoDateRange, change: DateRangeChange) => void;
  /**
   * `combined` (default) — ONE field, `start – end`. `split` — TWO fields side by side,
   * the spec's *Structure*. Either way one calendar serves both ends, and the field in
   * focus decides which end a click sets.
   */
  layout?: 'combined' | 'split';
  /** The quick-pick column on the right of the calendar (Figma `40245:262685`). */
  presets?: DateRangePreset[];
  minDate?: IsoDate | null;
  maxDate?: IsoDate | null;
  /**
   * The month the feature proposes when nothing is set (spec *Default panel positions* 2):
   * the left panel opens on it, the right on the eleventh month after it. Without it an
   * empty range opens on January and December of the current year.
   */
  referenceDate?: IsoDate | null;
  size?: FieldSize;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  /** Width of the combined field, or of EACH field when split. */
  width?: number | string;
  /** Trailing (default) or leading calendar glyph, on the field or on EACH field when split. */
  iconPlacement?: IconPlacement;
  /**
   * The calendar glyph. Default `true`; a dense inline field may drop it, since a press on
   * the field and ArrowDown open the calendar too.
   */
  icon?: boolean;
  /** Mount focused in the start with the calendar open. */
  autoOpen?: boolean;
  /**
   * A cleared range (both ends emptied) is published (default). `false` for a required
   * range: it is dropped like a half range, and the stored range shows again.
   */
  clearable?: boolean;
  /** Hears the calendar open and close. */
  onOpenChange?: (open: boolean) => void;
  /** Accessible names; default "Start date" / "End date". */
  startLabel?: string;
  endLabel?: string;
  placeholder?: string;
  'data-testid'?: string;
}

const later = (a: IsoDate | null | undefined, b: IsoDate | null | undefined) =>
  !a ? (b ?? null) : !b ? a : compareIso(a, b) >= 0 ? a : b;
const earlier = (a: IsoDate | null | undefined, b: IsoDate | null | undefined) =>
  !a ? (b ?? null) : !b ? a : compareIso(a, b) <= 0 ? a : b;

/** The end a calendar pick is setting, underlined while the calendar is open. */
const Segment = styled('span', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  display: 'inline-flex',
  alignSelf: 'stretch',
  alignItems: 'center',
  boxShadow: active ? `inset 0 -2px 0 ${theme.palette.primary.main}` : 'none',
}));

const Dash = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  flexShrink: 0,
}));

const SplitRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const NO_PROBLEMS: Record<Bound, TypedProblem | null> = { start: null, end: null };

/**
 * A RANGE OF DAYS — typed or picked, in one field or two, with optional presets and an
 * optional open end. The platform calendar spec's range mode; Figma range calendar
 * `26801:159901`, with presets `40245:262684`.
 *
 * THE FIELD HOLDS THE EDIT. While the auditor works on the range — picking, typing — it is
 * a draft inside the field (`rangeDraft.ts` says what each click does). The draft is
 * published once, when the edit ends, and only when it is settled:
 *
 * - the END picked (or a preset, or "Open end" with a start) — published, calendar closes;
 * - Enter in the end — published, closes;
 * - focus leaves the field and the calendar (a click away, Tab out) — published if settled;
 *   a half range is dropped and the stored range shows again;
 * - Escape — nothing is published; the stored range shows again.
 *
 * A typed entry that is refused (not a day, an end before the start, outside the bounds)
 * says why under the field and holds the edit back until it is fixed or Escaped.
 *
 * Picking the start with the pointer moves focus to the end, so the next click (or typing)
 * sets it; from the keyboard focus stays in the grid.
 */
export function DateRangeField({
  value: rawValue,
  openEnded: rawOpenEnded = false,
  allowOpenEnd = false,
  onChange,
  layout = 'combined',
  presets,
  minDate,
  maxDate,
  referenceDate,
  size = 'medium',
  disabled,
  readOnly,
  error,
  fullWidth,
  width,
  startLabel,
  endLabel,
  placeholder,
  iconPlacement = 'end',
  icon = true,
  autoOpen = false,
  clearable = true,
  onOpenChange,
  'data-testid': testId = 'date-range-field',
}: DateRangeFieldProps) {
  const { t } = useTranslation('dashboard', { keyPrefix: 'global' });
  const { start: storedStart, end: storedEnd } = normalizeRange(rawValue);
  const committed = useMemo(
    () => toDraft({ start: storedStart, end: storedEnd }, allowOpenEnd && rawOpenEnded),
    [storedStart, storedEnd, allowOpenEnd, rawOpenEnded],
  );
  const messageId = `${useId().replace(/:/g, '')}-message`;
  const startRef = useRef<HTMLInputElement | null>(null);
  const endRef = useRef<HTMLInputElement | null>(null);
  const inert = disabled || readOnly;
  const names = {
    start: startLabel ?? String(t('calendar_start_date')),
    end: endLabel ?? String(t('calendar_end_date')),
  };
  const openWord = allowOpenEnd ? String(t('calendar_open_word')) : undefined;
  const limits = useMemo(() => monthLimits(minDate, maxDate), [minDate, maxDate]);

  /** The edit in progress, or `null` when the field shows the stored range. */
  const [draft, setDraftState] = useState<RangeDraft | null>(null);
  /** The same, readable in the event that ends the edit (a blur lands before a re-render). */
  const draftRef = useRef<RangeDraft | null>(null);
  const setDraft = (next: RangeDraft | null) => {
    draftRef.current = next;
    setDraftState(next);
  };
  const shown = draft ?? committed;
  const current = () => draftRef.current ?? committed;

  const [problems, setProblemsState] = useState(NO_PROBLEMS);
  const problemsRef = useRef(NO_PROBLEMS);
  const setProblem = useCallback((bound: Bound, problem: TypedProblem | null) => {
    if (problemsRef.current[bound] === problem) return;
    problemsRef.current = { ...problemsRef.current, [bound]: problem };
    setProblemsState(problemsRef.current);
  }, []);
  const onStartProblem = useCallback(
    (p: TypedProblem | null) => setProblem('start', p),
    [setProblem],
  );
  const onEndProblem = useCallback((p: TypedProblem | null) => setProblem('end', p), [setProblem]);
  const blocked = () => Boolean(problemsRef.current.start || problemsRef.current.end);
  /** Bumped when the value is set again, so each input's text follows even for the same day. */
  const [revision, setRevision] = useState(0);

  const [active, setActive] = useState<Bound>('start');
  const [months, setMonths] = useState<MonthIndex[]>(() =>
    openingPanels({ count: 2, ...committed, referenceDate, limits }),
  );

  /** Publishes a settled draft that differs from what is stored. */
  const publish = (next: RangeDraft) => {
    const cleared = next.start === null && next.end === null && !next.openEnded;
    if (isSettled(next) && !draftsEqual(next, committed) && (clearable || !cleared)) {
      onChange(toRange(next), { openEnded: next.openEnded });
    }
  };

  /** The edit is over and kept: publish it if it can be, else let it go. */
  const finish = () => {
    const edit = draftRef.current;
    if (edit === null || blocked()) return;
    setDraft(null);
    // The inputs show the stored (or just published) range again, even where the draft
    // never reached their `value` — a cleared end, say, dropped with its half range.
    setRevision((n) => n + 1);
    publish(edit);
  };

  const popover = useCalendarPopover({ onOpenChange, onLeave: finish });

  const inputOf = (bound: Bound) => (bound === 'start' ? startRef : endRef).current;
  const panelOf = (bound: Bound) => (bound === 'end' ? 1 : 0);

  const show = (bound: Bound, focusGrid = false) => {
    if (inert) return;
    setActive(bound);
    if (popover.open) {
      if (focusGrid) popover.focusGrid(panelOf(bound));
      return;
    }
    setMonths(openingPanels({ count: 2, ...current(), referenceDate, limits }));
    popover.openCalendar(focusGrid ? panelOf(bound) : null);
  };

  /** A finished range: published, and the calendar closes. */
  const complete = (next: RangeDraft, returnTo: Bound) => {
    if (blocked()) {
      setDraft(next);
      return;
    }
    setDraft(null);
    setRevision((n) => n + 1);
    publish(next);
    popover.closeCalendar(inputOf(returnTo));
  };

  /** Escape: the edit is dropped and the stored range shows again. */
  const discard = (returnTo: Bound | null) => {
    setDraft(null);
    problemsRef.current = NO_PROBLEMS;
    setProblemsState(NO_PROBLEMS);
    setRevision((n) => n + 1);
    popover.closeCalendar(returnTo ? inputOf(returnTo) : null);
  };

  const autoOpened = useRef(false);
  const { openCalendar } = popover;
  useEffect(() => {
    if (!autoOpen || autoOpened.current) return;
    autoOpened.current = true;
    startRef.current?.focus();
    openCalendar();
  }, [autoOpen, openCalendar]);

  /** A replaced end no longer has a complaint about its text. */
  const clearReplaced = (before: RangeDraft, after: RangeDraft) => {
    if (before.start !== after.start) setProblem('start', null);
    if (before.end !== after.end || before.openEnded !== after.openEnded) setProblem('end', null);
  };

  const pick = (day: IsoDate) => {
    const fromKeyboard = popover.focusInCalendar();
    const before = current();
    const result = pickRangeDay(before, active, day);
    clearReplaced(before, result.draft);
    setRevision((n) => n + 1);
    setActive(result.active);
    if (result.done) return complete(result.draft, result.active);
    setDraft(result.draft);
    if (!fromKeyboard) inputOf(result.active)?.focus();
  };

  const chooseOpenEnd = () => {
    const before = current();
    const result = openRangeEnd(before);
    clearReplaced(before, result.draft);
    setActive(result.active);
    if (result.done) return complete(result.draft, 'end');
    setDraft(result.draft);
    inputOf('start')?.focus();
  };

  const commitTyped = (bound: Bound) => (day: IsoDate | null) => {
    const edit = current();
    setDraft(
      bound === 'end'
        ? { ...edit, end: day, openEnded: false }
        : { ...edit, start: day, openEnded: day === null ? false : edit.openEnded },
    );
  };

  const commitTypedOpen = () => {
    const edit = current();
    setDraft({ ...edit, end: null, openEnded: true });
  };

  const onEnter = (bound: Bound) => () => {
    if (bound === 'start') {
      setActive('end');
      inputOf('end')?.focus();
      return;
    }
    const edit = current();
    if (isSettled(edit)) {
      complete(edit, 'end');
    } else {
      setActive('start');
      inputOf('start')?.focus();
    }
  };

  const keyHandler = (bound: Bound) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      show(bound, true);
    } else if (event.key === 'Escape' && (popover.open || draftRef.current !== null)) {
      // Close without changes (spec *Keyboard*). The event stops here, so a surrounding
      // Menu stays open; with nothing to close it travels on.
      event.stopPropagation();
      discard(null);
    }
  };

  /** The end still missing, else the start: where a press on the padding or the glyph lands. */
  const nextBound = (): Bound =>
    shown.start !== null && shown.end === null && !shown.openEnded ? 'end' : 'start';

  const toggleFor = (bound: Bound | null) =>
    icon &&
    !readOnly && (
      <CalendarToggle
        open={popover.open}
        onToggle={() => {
          if (popover.open) {
            finish();
            return popover.closeCalendar();
          }
          // Opens with focus in the end being set, so typing and Escape work at once.
          const target = bound ?? nextBound();
          inputOf(target)?.focus();
          show(target);
        }}
        disabled={inert}
        size={size}
        controls={popover.popperId}
        testId={`${testId}${bound ? `-${bound}` : ''}-toggle`}
      />
    );

  const message =
    describeProblem(t, problems.start, { bound: 'start', ...shown }) ??
    describeProblem(t, problems.end, { bound: 'end', ...shown });
  const visibleMessage = size === 'medium';

  const segment = (bound: Bound) => (
    <SegmentInput
      ref={bound === 'start' ? startRef : endRef}
      value={shown[bound]}
      revision={revision}
      open={bound === 'end' && shown.openEnded}
      openWord={bound === 'end' ? openWord : undefined}
      onCommitOpen={commitTypedOpen}
      onCommit={commitTyped(bound)}
      onEnter={onEnter(bound)}
      onTypedDay={(day) => setMonths((panels) => followTypedDay(panels, bound, day, limits))}
      onProblemChange={bound === 'start' ? onStartProblem : onEndProblem}
      // A typed end before the start (or start after the end) is refused, not swapped:
      // the auditor typed one end on purpose and silently moving it would be a surprise.
      minDate={bound === 'end' ? later(minDate, shown.start) : minDate}
      maxDate={bound === 'start' && !shown.openEnded ? earlier(maxDate, shown.end) : maxDate}
      size={size}
      grow={layout === 'split'}
      placeholder={placeholder}
      aria-label={names[bound]}
      aria-describedby={problems[bound] ? messageId : undefined}
      title={!visibleMessage && problems[bound] ? (message ?? undefined) : undefined}
      aria-expanded={popover.open}
      aria-controls={popover.open ? popover.popperId : undefined}
      disabled={disabled}
      readOnly={readOnly}
      onFocus={() => setActive(bound)}
      onMouseDown={() => show(bound)}
      onKeyDown={keyHandler(bound)}
      data-testid={`${testId}-${bound}`}
    />
  );

  /** A press on a shell's padding lands in its input (or the end still missing). */
  const onPadPress = (bound: Bound | null) => (event: MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    const target = bound ?? nextBound();
    inputOf(target)?.focus();
    show(target);
  };

  const shellProps = {
    size,
    iconPlacement,
    error: Boolean(error || problems.start || problems.end),
    disabled,
    readOnly,
    sx: width !== undefined ? { width } : undefined,
  };

  const fields =
    layout === 'split' ? (
      <SplitRow>
        {(['start', 'end'] as const).map((bound, index) => (
          <Box key={bound} sx={{ display: 'contents' }}>
            {index === 1 && <Dash aria-hidden>–</Dash>}
            <FieldShell
              {...shellProps}
              error={Boolean(error || problems[bound])}
              toggle={toggleFor(bound)}
              active={popover.open && active === bound}
              fullWidth={fullWidth}
              onMouseDown={onPadPress(bound)}
              data-testid={`${testId}-${bound}-field`}
            >
              {segment(bound)}
            </FieldShell>
          </Box>
        ))}
      </SplitRow>
    ) : (
      <FieldShell
        {...shellProps}
        toggle={toggleFor(null)}
        active={popover.open}
        fullWidth={fullWidth}
        onMouseDown={onPadPress(null)}
        data-testid={testId}
      >
        <Segment active={popover.open && active === 'start'}>{segment('start')}</Segment>
        <Dash aria-hidden>–</Dash>
        <Segment active={popover.open && active === 'end'}>{segment('end')}</Segment>
        <Box sx={{ flex: 1 }} />
      </FieldShell>
    );

  const side = Boolean(presets?.length) || allowOpenEnd;

  return (
    <>
      <FieldRoot
        ref={popover.fieldRef}
        fullWidth={fullWidth}
        onBlur={popover.onBlurWithin}
        data-testid={layout === 'split' ? testId : `${testId}-root`}
      >
        {fields}
        <FieldMessage id={messageId} message={message} visible={visibleMessage} />
      </FieldRoot>
      <CalendarPopper
        ref={popover.paperRef}
        id={popover.popperId}
        open={popover.open}
        anchorEl={popover.fieldRef.current}
        fieldEl={popover.fieldRef.current}
        onClose={(reason) => {
          if (reason === 'escape') return discard(active);
          finish();
          popover.closeCalendar();
        }}
        onBlur={popover.onBlurWithin}
        label={`${names.start} – ${names.end}`}
        layoutKey={visibleMessage && message ? 'message' : 'plain'}
        data-testid={`${testId}-calendar`}
      >
        <CalendarGrid
          mode='range'
          value={toRange(shown)}
          openEnded={shown.openEnded}
          activeBound={active}
          onPick={pick}
          months={months}
          onMonthsChange={setMonths}
          minDate={minDate}
          maxDate={maxDate}
          autoFocusPanel={popover.focusPanelOnOpen}
        />
        {side && (
          <PresetList
            presets={presets}
            value={toRange(shown)}
            onSelect={(preset) => {
              const next = toDraft(normalizeRange(preset.value));
              clearReplaced(current(), next);
              complete(next, 'start');
            }}
            openEnd={
              allowOpenEnd ? { pressed: shown.openEnded, onSelect: chooseOpenEnd } : undefined
            }
          />
        )}
      </CalendarPopper>
    </>
  );
}
